import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { getTrialId } from "@/lib/api";
import { uploadVideoInParts, MAX_VIDEO_BYTES } from "@/lib/upload";
import { assistantApi, assistantError, streamAssistant, videoDuration } from "@/lib/assistant";

const SOURCE_SETTINGS = { template_id: "tmpl_hormozi", caption_enabled: true, face_track: true, clips_requested: 3, max_clip_seconds: 60 };
const readSaved = (key) => { try { return JSON.parse(localStorage.getItem(key) || "null"); } catch { return null; } };
const store = (key, value) => { try { value === null ? localStorage.removeItem(key) : localStorage.setItem(key, JSON.stringify(value)); } catch {} };

export function useClipAssistant() {
  const { user, loading: authLoading } = useAuth();
  const owner = user?.id || `guest:${getTrialId()}`;
  const [conversation, setConversation] = useState(null), [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(null), [error, setError] = useState(null), [stream, setStream] = useState("");
  const [progress, setProgress] = useState(null), [retryRequest, setRetryRequest] = useState(null);
  const [retryFile, setRetryFile] = useState(null);
  const epoch = useRef(0), current = useRef(null), chatController = useRef(null);
  current.current = conversation;
  const sessionKey = `clippedup_ai_conversation:${owner}`;

  const accept = useCallback((data, version) => {
    if (epoch.current !== version) return;
    setConversation(data);
    store(sessionKey, data.id);
    const last = data.messages.at(-1);
    setRetryRequest(!data.project_id && last?.role === "user" && last.request_id
      ? { text: last.content, request_id: last.request_id } : null);
  }, [sessionKey]);

  const refreshHistory = useCallback(async (version) => {
    const list = await assistantApi.list();
    if (epoch.current === version) setHistory(list);
    return list;
  }, []);

  const perform = async (kind, action) => {
    const version = epoch.current, id = current.current?.id;
    setBusy(kind); setError(null);
    try {
      const result = await action(version);
      if (result?.id) accept(result, version);
      return result;
    } catch (failure) {
      if (epoch.current === version) {
        setError(assistantError(failure));
        if (id) {
          try { accept(await assistantApi.get(id), version); } catch {}
        }
      }
      return null;
    } finally {
      if (epoch.current === version) { setBusy(null); setStream(""); }
    }
  };

  const initialize = useCallback(async () => {
    const version = ++epoch.current;
    chatController.current?.abort();
    setConversation(null); setHistory([]); setError(null); setBusy("loading");
    setProgress(null); setRetryFile(null); setRetryRequest(null); setStream("");
    try {
      const list = await refreshHistory(version);
      if (epoch.current !== version) return;
      const saved = readSaved(sessionKey);
      const id = list.find((item) => item.id === saved)?.id || list[0]?.id;
      accept(id ? await assistantApi.get(id) : await assistantApi.create(), version);
      await refreshHistory(version);
    } catch (failure) {
      if (epoch.current === version) setError(assistantError(failure));
    } finally { if (epoch.current === version) setBusy(null); }
  }, [accept, refreshHistory, sessionKey]);

  useEffect(() => {
    if (authLoading) { setBusy("loading"); return; }
    initialize();
    return () => { epoch.current += 1; chatController.current?.abort(); };
  }, [initialize, authLoading]);

  const activeProject = conversation?.project && !["completed", "failed"].includes(conversation.project.status);
  useEffect(() => {
    if (!conversation?.id || (!activeProject && !conversation.busy)) return;
    const id = conversation.id, version = epoch.current;
    let active = true, timer;
    const poll = async () => {
      try {
        const data = await assistantApi.get(id);
        if (active && epoch.current === version && current.current?.id === id) {
          accept(data, version);
          setError((value) => value?.startsWith("Connection interrupted.") ? null : value);
        }
      } catch {
        if (active && epoch.current === version) setError("Connection interrupted. We’ll keep checking; your conversation and saved clips are safe.");
      }
      if (active) timer = setTimeout(poll, 3000);
    };
    timer = setTimeout(poll, 3000);
    return () => { active = false; clearTimeout(timer); };
  }, [conversation?.id, conversation?.busy, activeProject, accept]);

  const openConversation = async (id) => {
    if (busy) return;
    setProgress(null); setRetryFile(null); setRetryRequest(null);
    return perform("loading", async () => assistantApi.get(id));
  };

  const newConversation = async () => {
    if (busy) return;
    setProgress(null); setRetryFile(null); setRetryRequest(null);
    return perform("loading", async (version) => {
      const data = await assistantApi.create();
      await refreshHistory(version);
      return data;
    });
  };

  const send = async (text, previous = null) => {
    if (!text.trim() || busy || !conversation || conversation.project_id || conversation.busy) return;
    const input = previous || { text: text.trim(), request_id: crypto.randomUUID().replaceAll("-", "") };
    setRetryRequest(input);
    if (!previous) setConversation((value) => ({ ...value, messages: [...value.messages,
      { id: input.request_id, role: "user", content: input.text, request_id: input.request_id }] }));
    return perform("message", async (version) => {
      const controller = new AbortController();
      chatController.current = controller;
      const timeout = setTimeout(() => controller.abort(), 110000);
      try {
        const data = await streamAssistant(conversation.id, { ...input, revision: conversation.revision },
          (delta) => { if (epoch.current === version) setStream((value) => value + delta); }, controller.signal);
        await refreshHistory(version);
        return data;
      } finally { clearTimeout(timeout); }
    });
  };

  const attach = async (file) => {
    if (!file || busy || !conversation || conversation.project_id) return;
    if (!file.type.startsWith("video/") && !/\.(mp4|mov|webm|mkv|m4v|avi)$/i.test(file.name)) {
      setError("Please choose a video file."); return;
    }
    if (!file.size || file.size > MAX_VIDEO_BYTES) {
      setError("Choose a video larger than 0 bytes and no larger than 1 GB."); return;
    }
    const version = epoch.current, id = conversation.id;
    const key = `clippedup_ai_upload:${owner}:${id}`;
    const signature = `${file.name}:${file.size}:${file.lastModified}`;
    const previous = readSaved(key);
    const descriptor = previous?.signature === signature ? previous : { id: crypto.randomUUID().replaceAll("-", ""), signature };
    store(key, descriptor);
    setRetryFile(file); setProgress({ progress: 0, savedBytes: 0, size: file.size, phase: "Preparing your video…" });
    return perform("upload", async () => {
      const duration = videoDuration(file);
      const result = await uploadVideoInParts(file, { ...SOURCE_SETTINGS, trial_id: getTrialId() }, descriptor, (update) => {
        if (epoch.current === version) setProgress((value) => ({ ...value, ...update }));
      }, { deferProcessing: true });
      const data = await assistantApi.attach(id, conversation.revision, result.upload_id, await duration);
      store(key, null);
      if (epoch.current === version) { setProgress(null); setRetryFile(null); }
      return data;
    });
  };

  const usePreset = (brief) => perform('saving', async (version) => {
    const created = await assistantApi.create();
    const updated = await assistantApi.save(created.id, created.revision, brief);
    await refreshHistory(version);
    if (epoch.current === version) { setProgress(null); setRetryFile(null); }
    return updated;
  });

  const save = (brief) => perform("saving", () => assistantApi.save(conversation.id, conversation.revision, brief));
  const confirm = () => perform("confirming", async () => {
    const data = await assistantApi.confirm(conversation.id, conversation.revision);
    return data;
  });

  return { user, conversation, history, busy, error, stream, progress, retryFile, retryRequest,
    initialize, openConversation, newConversation, usePreset, send, attach, save, confirm,
    stopReply: () => chatController.current?.abort() };
}
